// The gazetteer as a list: every stored place alphabetically, its curated flag,
// and one column per facility.
//
// The map above answers "what is near here"; this answers "which of these have
// fuel" -- a question a popup can never answer, because it shows one row at a
// time. That is why it is a column per facility rather than the popup's badge
// per facility: a fixed column is the thing you can run your eye down.
//
// Presentational and read-only. It loads nothing and navigates nowhere; the
// page owns both (onSelect is Places.jsx's handleEdit).
//
// TWO DELIBERATE DEPARTURES from the house rules:
//
//   The FULL name, not placeDisplayName. Every other table cell in the app
//   shows the short name, but this page is the only surface where the gazetteer
//   is visible AS a database -- the row is the identity, two "Friday Harbor"s
//   have to be told apart, and A-Z order has to be self-evident from what is
//   drawn.
//
//   A false facility renders exactly like a null one: an empty cell. The model
//   is explicit that a blank means "nobody has said" rather than "no", and an
//   unlit icon would assert a denial the database has not made. That matters
//   most for the negative flags -- an unlit No Landing badge reads as "landing
//   is fine", which is the opposite of what the blank means.

import { useMemo } from 'react';
import Table from 'react-bootstrap/Table';
import { FACILITY_FIELDS } from '../../fleet-shared/PlaceFields.mjs';

// Wide enough for a glyph, narrow enough that a good many of them fit beside a
// name before `responsive` starts scrolling. Not a budget: the column count is
// whatever FACILITY_FIELDS is long, and the table is built to grow with it.
const FACILITY_COL_WIDTH = 36;

// The field's one-glyph rendering. Always decorative here -- every caller pairs
// it with the field name, either as visually-hidden header text or as the
// cell's own aria-label -- so it never carries an accessible name of its own.
//
// A field with no `symbol` falls back to its first letter rather than drawing
// nothing: a facility added without one should look unfinished, not
// produce a column that is blank in every row including the header.
const FacilitySymbol = ({ field }) => {
  const icon = field.symbol?.icon;
  if (icon) return <i className={`fa-solid fa-${icon}`} aria-hidden="true"></i>;
  const letter = field.symbol?.letter || (field.name || '?').charAt(0);
  return <span className="fw-bold small" aria-hidden="true">{letter}</span>;
};

// A meaningful icon, the same idiom as PlaceVisits' OnMapFlag: title for the
// hover, aria-label + role for everyone else.
const FacilityFlag = ({ field }) => (
  <span title={field.name} aria-label={field.name} role="img">
    <FacilitySymbol field={field} />
  </span>
);

const PlaceRow = ({ place, onSelect }) => (
  <tr onClick={() => onSelect(place.id)} role="button" style={{ cursor: 'pointer' }}>
    <td className="align-middle entered-data">{place.name}</td>
    <td className="align-middle text-center">
      {place.curated === true && (
        <i className="fa-solid fa-check" title="Curated" aria-label="Curated" role="img"></i>
      )}
    </td>
    {FACILITY_FIELDS.map((field) => (
      <td key={field.key} className="align-middle text-center">
        {place[field.key] === true && <FacilityFlag field={field} />}
      </td>
    ))}
  </tr>
);

const PlacesTable = ({ places, onSelect }) => {
  // Copied before sorting: `places` is the page's loaded row array, handed to
  // the map above as well.
  const sorted = useMemo(
    () =>
      [...(places || [])].sort((a, b) =>
        (a?.name || '').localeCompare(b?.name || '', undefined, {
          sensitivity: 'base',
          numeric: true,
        })
      ),
    [places]
  );

  return (
    // `responsive` is what makes a column per facility survive a phone: it scrolls
    // sideways rather than crushing the name column.
    <Table responsive hover className="align-middle mb-0">
      <thead className="bg-body-tertiary">
        <tr>
          <th scope="col">Name</th>
          <th scope="col" className="text-center">Curated</th>
          {FACILITY_FIELDS.map((field) => (
            // The glyph for the hover, the name for the accessibility tree --
            // `title` has no keyboard or touch affordance, so it can't be the
            // column's only label.
            <th
              key={field.key}
              scope="col"
              className="text-center"
              style={{ width: FACILITY_COL_WIDTH }}
              title={field.name}
            >
              <FacilitySymbol field={field} />
              <span className="visually-hidden">{field.name}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((place) => (
          <PlaceRow key={place.id} place={place} onSelect={onSelect} />
        ))}
      </tbody>
    </Table>
  );
};

export default PlacesTable;
